import { environments, type Environments, apiTokens, apiUrls } from "../constants";
import type { JsqFlagsGlobalThis } from "../types";

declare let globalThis: JsqFlagsGlobalThis;

const ipAddressExp = /^(?:\d{1,3}\.){3}\d{1,3}$/;
const previewExp = /^pr[a-zA-Z\-_]+\d+$/;

export type EnvironmentInfo = {
  environmentType: Environments;
  environmentKey?: string;
};

export type ApiInfo = {
  apiToken?: string;
  apiUrl?: string;
};

export function identifyEnvironment(): EnvironmentInfo & ApiInfo {
  const isMobileEnv = !!globalThis.__jsqFlagsMobile__;
  const { environmentType, environmentKey } = isMobileEnv
    ? parseMobileEnvironment()
    : parseEnvironmentFromUrl();
  const { apiToken, apiUrl } = getApiSettings(environmentType);
  return {
    environmentType,
    environmentKey,
    apiToken,
    apiUrl,
  };
}

function parseEnvironmentFromUrl(): EnvironmentInfo {
  const { hostname } = location;
  if (process.env.CYPRESS_CI) {
    return {
      environmentType: environments.circleCI,
    };
  }

  if (["development", "test"].includes(process.env.NODE_ENV ?? "")) {
    if (hostname === "localhost" || ipAddressExp.test(hostname)) {
      return {
        environmentType: environments.development,
      };
    }
  }

  if (process.env.NODE_ENV === "development" && !!__DEV_HOSTNAME__) {
    return {
      environmentType: environments.development,
      environmentKey: __DEV_HOSTNAME__,
    };
  }

  const splitHostname = hostname.split(".");
  if (splitHostname.length < 3) {
    return {
      environmentType: environments.unknown,
    };
  }

  const maybeEnv = splitHostname.at(-3);

  switch (maybeEnv) {
    case "cit":
      return {
        environmentType: environments.cit,
      };
    case "prodops":
      return {
        environmentType: environments.prodops,
      };
    case "staging":
      return {
        environmentType: environments.staging,
      };
    case "test":
      return {
        environmentType: environments.test,
      };
    case "dev":
    case "local":
      return {
        environmentType: environments.development,
      };
    case "app":
    case "admin":
      return {
        environmentType: environments.production,
      };
  }

  if (maybeEnv?.match(previewExp)) {
    // preview urls have the format <subdomain>.pr-<prNumber>.junipersquare.us
    // ipe urls have the format <subdomain>.pr<servicePrefix><prNumber>.junipersquare.us
    // servicePrefix can include dashes but not numbers
    //
    return {
      environmentType: environments.preview,
      environmentKey: maybeEnv,
    };
  }

  if (splitHostname.length === 3) {
    return {
      environmentType: environments.production,
    };
  }
  return {
    environmentType: environments.unknown,
  };
}

function parseMobileEnvironment(): EnvironmentInfo {
  const { __jsqFlagsMobile__ } = globalThis;
  switch (__jsqFlagsMobile__) {
    case "local":
    case "qa":
      return {
        environmentType: environments.cit,
      };
    case "test":
      return {
        environmentType: environments.test,
      };
    case "production":
      return {
        environmentType: environments.production,
      };
    default:
      return {
        environmentType: environments.unknown,
      };
  }
}

function getApiSettings(environmentType: Environments): ApiInfo {
  if (environmentType === environments.development && process.env.UNLEASH_LOCAL === "1") {
    return {
      apiToken: apiTokens.development,
      apiUrl: apiUrls.development,
    };
  }

  switch (environmentType) {
    case environments.test:
    case environments.staging:
    case environments.preview:
    case environments.development: // if UNLEASH_LOCAL env var is not set, point dev to test instance
      return {
        apiToken: apiTokens.test,
        apiUrl: apiUrls.test,
      };

    case environments.cit:
      return {
        apiToken: apiTokens.cit,
        apiUrl: apiUrls.cit,
      };
    case environments.production:
      return {
        apiToken: apiTokens.production,
        apiUrl: apiUrls.production,
      };
    case environments.prodops:
      return {
        apiToken: apiTokens.prodops,
        apiUrl: apiUrls.prodops,
      };
    case environments.circleCI:
      return {
        apiToken: apiTokens.circleCI,
        apiUrl: apiUrls.circleCI,
      };
    default:
      return {
        apiToken: undefined,
        apiUrl: undefined,
      };
  }
}
